import React, { useRef, useState, useEffect } from 'react';
import authApi, { TutorialResponse, VideoResponse } from '../../../services/authApi';
import { toast } from 'react-toastify';
import '../tool-page.css';
import '../AdminTutorialsPage.css';

interface TutorialForm {
  title: string; description: string; badge: string;
  tags: string; orderIndex: number; isFree: boolean;
}
interface VideoForm {
  title: string; description: string; videoUrl: string; durationMin: number; orderIndex: number;
}

const BLANK_TUTORIAL: TutorialForm = { title: '', description: '', badge: '📹', tags: '', orderIndex: 0, isFree: false };
const BLANK_VIDEO: VideoForm    = { title: '', description: '', videoUrl: '', durationMin: 0, orderIndex: 1 };

const AdminTutorialsTab: React.FC = () => {
  const [tutorials, setTutorials]     = useState<TutorialResponse[]>([]);
  const [loading, setLoading]         = useState(true);
  const [expandedId, setExpandedId]   = useState<number | null>(null);
  const [videosByTutorial, setVideosByTutorial] = useState<Record<number, VideoResponse[]>>({});

  const [tutModalOpen, setTutModalOpen] = useState(false);
  const [tutEditing, setTutEditing]     = useState<TutorialResponse | null>(null);
  const [tutForm, setTutForm]           = useState<TutorialForm>(BLANK_TUTORIAL);
  const [tutSaving, setTutSaving]       = useState(false);

  const [vidModalOpen, setVidModalOpen] = useState(false);
  const [vidTutorialId, setVidTutorialId] = useState<number | null>(null);
  const [vidEditing, setVidEditing]     = useState<VideoResponse | null>(null);
  const [vidForm, setVidForm]           = useState<VideoForm>(BLANK_VIDEO);
  const [vidSaving, setVidSaving]       = useState(false);
  const [uploading, setUploading]       = useState(false);
  const [uploadPct, setUploadPct]       = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { loadTutorials(); }, []);

  const loadTutorials = () => {
    setLoading(true);
    authApi.adminGetTutorials()
      .then((r) => setTutorials(r.data || []))
      .catch(() => toast.error('Failed to load tutorials'))
      .finally(() => setLoading(false));
  };

  const loadVideos = async (tutorialId: number) => {
    if (videosByTutorial[tutorialId]) return;
    try {
      const r = await authApi.adminGetVideos(tutorialId);
      setVideosByTutorial((prev) => ({ ...prev, [tutorialId]: r.data || [] }));
    } catch {
      toast.error('Failed to load videos');
    }
  };

  const toggleExpand = async (id: number) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    await loadVideos(id);
  };

  const openCreateTutorial = () => {
    setTutEditing(null); setTutForm(BLANK_TUTORIAL); setTutModalOpen(true);
  };
  const openEditTutorial = (t: TutorialResponse) => {
    setTutEditing(t);
    setTutForm({ title: t.title, description: t.description || '', badge: t.badge, tags: t.tags.join(','), orderIndex: t.orderIndex, isFree: t.isFree });
    setTutModalOpen(true);
  };
  const saveTutorial = async () => {
    if (!tutForm.title.trim()) { toast.error('Title is required'); return; }
    setTutSaving(true);
    try {
      if (tutEditing) {
        await authApi.adminUpdateTutorial(tutEditing.id, tutForm);
        toast.success('Tutorial updated');
      } else {
        await authApi.adminCreateTutorial(tutForm);
        toast.success('Tutorial created');
      }
      setTutModalOpen(false); loadTutorials();
    } catch { toast.error('Save failed'); }
    finally { setTutSaving(false); }
  };
  const toggleActive = async (id: number, current: boolean) => {
    try {
      await authApi.adminToggleTutorial(id);
      toast.success(current ? 'Tutorial deactivated' : 'Tutorial activated');
      loadTutorials();
    } catch { toast.error('Failed to update status'); }
  };

  const toggleFree = async (id: number, current: boolean) => {
    try {
      await authApi.adminToggleTutorialFree(id);
      toast.success(current ? 'Changed to Pro' : 'Changed to Free');
      loadTutorials();
    } catch { toast.error('Failed to update status'); }
  };

  const deleteTutorial = async (id: number, title: string) => {
    if (!window.confirm(`Delete "${title}"? It will be hidden from all users.`)) return;
    try {
      await authApi.adminDeleteTutorial(id);
      setTutorials((prev) => prev.filter((t) => t.id !== id));
      if (expandedId === id) setExpandedId(null);
      toast.success('Tutorial deleted');
    } catch { toast.error('Delete failed'); }
  };

  const openAddVideo = (tutorialId: number) => {
    setVidTutorialId(tutorialId); setVidEditing(null); setVidForm(BLANK_VIDEO); setVidModalOpen(true);
  };
  const openEditVideo = (tutorialId: number, v: VideoResponse) => {
    setVidTutorialId(tutorialId); setVidEditing(v);
    setVidForm({ title: v.title, description: v.description || '', videoUrl: v.videoUrl || '', durationMin: v.durationMin, orderIndex: v.orderIndex });
    setVidModalOpen(true);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadPct(10);
    const interval = setInterval(() => setUploadPct((p) => Math.min(p + 8, 90)), 600);
    try {
      const r = await authApi.adminUploadVideo(file);
      setVidForm((f) => ({ ...f, videoUrl: r.data }));
      toast.success('Video uploaded to DO Spaces'); setUploadPct(100);
    } catch { toast.error('Upload failed'); }
    finally {
      clearInterval(interval); setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const saveVideo = async () => {
    if (!vidForm.title.trim()) { toast.error('Title is required'); return; }
    if (!vidForm.videoUrl.trim()) { toast.error('Video URL is required — upload a file or paste a URL'); return; }
    if (!vidTutorialId) return;
    setVidSaving(true);
    try {
      if (vidEditing) {
        const r = await authApi.adminUpdateVideo(vidEditing.id, vidForm);
        setVideosByTutorial((prev) => ({
          ...prev,
          [vidTutorialId]: (prev[vidTutorialId] || []).map((v) => v.id === vidEditing.id ? r.data : v),
        }));
        toast.success('Video updated');
      } else {
        const r = await authApi.adminAddVideo(vidTutorialId, vidForm);
        setVideosByTutorial((prev) => ({
          ...prev,
          [vidTutorialId]: [...(prev[vidTutorialId] || []), r.data],
        }));
        toast.success('Video added');
      }
      setVidModalOpen(false);
    } catch { toast.error('Save failed'); }
    finally { setVidSaving(false); }
  };

  const deleteVideo = async (tutorialId: number, videoId: number) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await authApi.adminDeleteVideo(videoId);
      setVideosByTutorial((prev) => ({ ...prev, [tutorialId]: (prev[tutorialId] || []).filter((v) => v.id !== videoId) }));
      toast.success('Video deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="tool-page" style={{ padding: 0 }}>
      <div className="tool-header">
        <div className="tool-header-left">
          <p className="tool-eyebrow">Content</p>
          <h1 className="tool-title">Tutorial Management</h1>
          <p className="tool-subtitle">Create and manage video tutorials. Videos are hosted on DigitalOcean Spaces.</p>
        </div>
        <button className="adm-btn adm-btn-primary" onClick={openCreateTutorial}>+ New Tutorial</button>
      </div>

      <div className="tool-stats">
        <div className="stat-chip"><div><div className="stat-chip-val">{tutorials.length}</div><div className="stat-chip-label">Tutorials</div></div></div>
        <div className="stat-chip"><div><div className="stat-chip-val">{tutorials.filter((t) => t.isFree).length}</div><div className="stat-chip-label">Free</div></div></div>
        <div className="stat-chip"><div><div className="stat-chip-val">{tutorials.filter((t) => !t.isFree).length}</div><div className="stat-chip-label">Pro</div></div></div>
      </div>

      {loading ? (
        <div className="adm-loading">Loading tutorials...</div>
      ) : (
        <div className="adm-list">
          {tutorials.map((t) => (
            <div key={t.id} className={`adm-tutorial-row ${expandedId === t.id ? 'adm-tutorial-row--open' : ''}`}>
              <div className="adm-tut-header">
                <button className="adm-tut-expand" onClick={() => toggleExpand(t.id)}>
                  <span className="adm-tut-badge">{t.badge}</span>
                  <div className="adm-tut-info">
                    <div className="adm-tut-title">{t.title}</div>
                    <div className="adm-tut-meta">
                      <span className="adm-meta-txt">{t.videoCount} video{t.videoCount !== 1 ? 's' : ''}</span>
                      {t.tags.length > 0 && <span className="adm-meta-txt">{t.tags.join(' · ')}</span>}
                    </div>
                  </div>
                  <ChevronIcon open={expandedId === t.id} />
                </button>
                <div className="adm-tut-actions">
                  <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => openEditTutorial(t)}>Edit</button>
                  <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => deleteTutorial(t.id, t.title)}>Del</button>
                  {/* Active / Inactive toggle */}
                  <button
                    className={`adm-status-chip ${t.isActive ? 'adm-chip-active' : 'adm-chip-inactive'}`}
                    onClick={() => toggleActive(t.id, t.isActive)}
                    title={t.isActive ? 'Click to deactivate (hide from users)' : 'Click to activate (show to users)'}
                  >
                    <span className="adm-chip-dot" />
                    {t.isActive ? 'Active' : 'Inactive'}
                  </button>
                  {/* Free / Pro toggle */}
                  <button
                    className={`adm-status-chip ${t.isFree ? 'adm-chip-free' : 'adm-chip-pro'}`}
                    onClick={() => toggleFree(t.id, t.isFree)}
                    title={t.isFree ? 'Click to make Pro (require subscription)' : 'Click to make Free (open to all)'}
                  >
                    {t.isFree ? 'Free' : 'Pro'}
                  </button>
                  <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => openAddVideo(t.id)}>+ Video</button>
                </div>
              </div>

              {expandedId === t.id && (
                <div className="adm-videos">
                  {!videosByTutorial[t.id] ? (
                    <div className="adm-vid-loading">Loading videos...</div>
                  ) : videosByTutorial[t.id].length === 0 ? (
                    <div className="adm-vid-empty">No videos yet. Click "+ Video" to add the first one.</div>
                  ) : (
                    videosByTutorial[t.id].map((v, idx) => (
                      <div key={v.id} className="adm-vid-row">
                        <span className="adm-vid-num">{idx + 1}</span>
                        <div className="adm-vid-info">
                          <div className="adm-vid-title">{v.title}</div>
                          {v.description && <div className="adm-vid-desc">{v.description}</div>}
                          <div className="adm-vid-url" title={v.videoUrl || ''}>{v.videoUrl ? '✓ ' + v.videoUrl.split('/').pop() : '— no URL'}</div>
                        </div>
                        <span className="adm-vid-dur">{v.durationMin}m</span>
                        <div className="adm-vid-actions">
                          <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => openEditVideo(t.id, v)}>Edit</button>
                          <button className="adm-btn adm-btn-danger adm-btn-sm" onClick={() => deleteVideo(t.id, v.id)}>Del</button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
          {tutorials.length === 0 && (
            <div className="adm-empty">No tutorials yet. Click "+ New Tutorial" to create one.</div>
          )}
        </div>
      )}

      {/* Tutorial modal */}
      {tutModalOpen && (
        <div className="adm-modal-overlay" onClick={() => setTutModalOpen(false)}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h3>{tutEditing ? 'Edit Tutorial' : 'New Tutorial'}</h3>
              <button className="adm-modal-close" onClick={() => setTutModalOpen(false)}>✕</button>
            </div>
            <div className="adm-modal-body">
              <div className="adm-field-row">
                <div className="adm-field adm-field--sm">
                  <label>Badge Emoji</label>
                  <input value={tutForm.badge} onChange={(e) => setTutForm({ ...tutForm, badge: e.target.value })} placeholder="📹" maxLength={4} />
                </div>
                <div className="adm-field adm-field--grow">
                  <label>Title *</label>
                  <input value={tutForm.title} onChange={(e) => setTutForm({ ...tutForm, title: e.target.value })} placeholder="e.g. Bull Flag Pattern Deep Dive" />
                </div>
              </div>
              <div className="adm-field">
                <label>Description</label>
                <textarea rows={3} value={tutForm.description} onChange={(e) => setTutForm({ ...tutForm, description: e.target.value })} placeholder="Brief overview of this tutorial topic" />
              </div>
              <div className="adm-field-row">
                <div className="adm-field adm-field--grow">
                  <label>Tags (comma-separated)</label>
                  <input value={tutForm.tags} onChange={(e) => setTutForm({ ...tutForm, tags: e.target.value })} placeholder="Pattern,Intermediate" />
                </div>
                <div className="adm-field adm-field--sm">
                  <label>Order</label>
                  <input type="number" value={tutForm.orderIndex} onChange={(e) => setTutForm({ ...tutForm, orderIndex: +e.target.value })} min={0} />
                </div>
              </div>
              <div className="adm-toggle-row">
                <label className="adm-toggle-label">
                  <input type="checkbox" checked={tutForm.isFree} onChange={(e) => setTutForm({ ...tutForm, isFree: e.target.checked })} />
                  <span>Free tutorial (visible without Pro subscription)</span>
                </label>
              </div>
            </div>
            <div className="adm-modal-footer">
              <button className="adm-btn adm-btn-ghost" onClick={() => setTutModalOpen(false)}>Cancel</button>
              <button className="adm-btn adm-btn-primary" onClick={saveTutorial} disabled={tutSaving}>
                {tutSaving ? 'Saving...' : tutEditing ? 'Update Tutorial' : 'Create Tutorial'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video modal */}
      {vidModalOpen && (
        <div className="adm-modal-overlay" onClick={() => setVidModalOpen(false)}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h3>{vidEditing ? 'Edit Video' : 'Add Video'}</h3>
              <button className="adm-modal-close" onClick={() => setVidModalOpen(false)}>✕</button>
            </div>
            <div className="adm-modal-body">
              <div className="adm-field">
                <label>Video Title *</label>
                <input value={vidForm.title} onChange={(e) => setVidForm({ ...vidForm, title: e.target.value })} placeholder="e.g. Anatomy of a Bull Flag" />
              </div>
              <div className="adm-field">
                <label>Description</label>
                <textarea rows={2} value={vidForm.description} onChange={(e) => setVidForm({ ...vidForm, description: e.target.value })} placeholder="What this video covers" />
              </div>
              <div className="adm-field">
                <label>Video File <span className="adm-label-sub">(uploads to DigitalOcean Spaces)</span></label>
                <div className="adm-upload-row">
                  <input ref={fileRef} type="file" accept="video/*" onChange={handleFileChange} className="adm-file-input" disabled={uploading} />
                  {uploading && (
                    <div className="adm-upload-bar">
                      <div className="adm-upload-bar-fill" style={{ width: `${uploadPct}%` }} />
                    </div>
                  )}
                </div>
              </div>
              <div className="adm-field">
                <label>Video URL <span className="adm-label-sub">(auto-filled after upload, or paste embed URL)</span></label>
                <input value={vidForm.videoUrl} onChange={(e) => setVidForm({ ...vidForm, videoUrl: e.target.value })} placeholder="https://riyu.fra1.digitaloceanspaces.com/files/community-tutorials/..." />
              </div>
              <div className="adm-field-row">
                <div className="adm-field adm-field--sm">
                  <label>Duration (min)</label>
                  <input type="number" value={vidForm.durationMin} onChange={(e) => setVidForm({ ...vidForm, durationMin: +e.target.value })} min={0} />
                </div>
                <div className="adm-field adm-field--sm">
                  <label>Order</label>
                  <input type="number" value={vidForm.orderIndex} onChange={(e) => setVidForm({ ...vidForm, orderIndex: +e.target.value })} min={1} />
                </div>
              </div>
            </div>
            <div className="adm-modal-footer">
              <button className="adm-btn adm-btn-ghost" onClick={() => setVidModalOpen(false)}>Cancel</button>
              <button className="adm-btn adm-btn-primary" onClick={saveVideo} disabled={vidSaving || uploading}>
                {vidSaving ? 'Saving...' : vidEditing ? 'Update Video' : 'Add Video'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTutorialsTab;

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      width="16" height="16" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.18s', flexShrink: 0 }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
